/**
 * Device detection utilities
 * Detects device types based on user agent to provide appropriate UX
 */

/**
 * Checks if the current device is an iPad
 * Uses user agent detection to identify iPad regardless of screen size
 * @returns true if the device is an iPad
 */
export function isIPad(): boolean {
  if (typeof window === "undefined" || typeof navigator === "undefined") {
    return false;
  }

  const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera;
  
  // Check for iPad in user agent
  // Note: iPadOS 13+ may report as "Macintosh" in desktop mode, so we also check for touch support
  const isIPadUserAgent = /iPad/i.test(userAgent);
  const isMacWithTouch = /Macintosh/i.test(userAgent) && navigator.maxTouchPoints > 1;
  
  return isIPadUserAgent || isMacWithTouch;
}

/**
 * Checks if the current device is an iPhone
 * @returns true if the device is an iPhone
 */
export function isIPhone(): boolean {
  if (typeof window === "undefined" || typeof navigator === "undefined") {
    return false;
  }

  const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera;
  return /iPhone/i.test(userAgent);
}

/**
 * Checks if the current device is an iPod
 * @returns true if the device is an iPod
 */
export function isIPod(): boolean {
  if (typeof window === "undefined" || typeof navigator === "undefined") {
    return false;
  }

  const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera;
  return /iPod/i.test(userAgent);
}

/**
 * Checks if the current device is any iOS device (iPhone, iPad, or iPod)
 * @returns true if the device is iOS
 */
export function isIOS(): boolean {
  return isIPhone() || isIPad() || isIPod();
}

/**
 * Checks if the current device is an Android tablet
 * @returns true if the device is an Android tablet
 */
export function isAndroidTablet(): boolean {
  if (typeof window === "undefined" || typeof navigator === "undefined") {
    return false;
  }

  const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera;
  return /Android/i.test(userAgent) && !/Mobile/i.test(userAgent);
}

/**
 * Checks if the current device is any tablet (iPad or Android tablet)
 * @returns true if the device is a tablet
 */
export function isTablet(): boolean {
  return isIPad() || isAndroidTablet();
}

/**
 * Checks if the current device is a mobile phone (iPhone, iPod, or Android phone)
 * @returns true if the device is a mobile phone
 */
export function isMobilePhone(): boolean {
  if (typeof window === "undefined" || typeof navigator === "undefined") {
    return false;
  }

  const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera;
  
  // iPhone or iPod
  if (isIPhone() || isIPod()) {
    return true;
  }
  
  // Android phone (has "Mobile" in user agent)
  return /Android/i.test(userAgent) && /Mobile/i.test(userAgent);
}

/**
 * Checks if the current device should use mobile UX
 * This includes mobile phones AND tablets (like iPad)
 * @returns true if the device should use mobile UX
 */
export function shouldUseMobileUX(): boolean {
  return isMobilePhone() || isTablet();
}
